import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import AuthContext from '../components/shared/AuthContext';
import { subscribeRealtimeInvalidate } from '../realtime/realtimeBus';

export const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const { apiClient, user } = useContext(AuthContext);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!apiClient || !user) {
      setUnreadCount(0);
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.get('/api/notifications', { params: { limit: 25 } });
      setUnreadCount(Number(res.data?.unreadCount ?? 0));
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch {
      setUnreadCount(0);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [apiClient, user]);

  const refreshUnreadOnly = useCallback(async () => {
    if (!apiClient || !user) {
      setUnreadCount(0);
      return;
    }
    try {
      const res = await apiClient.get('/api/notifications/unread-count');
      setUnreadCount(Number(res.data?.unreadCount ?? 0));
    } catch {
      /* keep last count */
    }
  }, [apiClient, user]);

  useEffect(() => {
    if (user) refresh();
    else {
      setUnreadCount(0);
      setItems([]);
    }
  }, [user, refresh]);

  useEffect(() => {
    if (!user || !apiClient) return undefined;
    const tick = () => {
      if (document.visibilityState === 'visible') refreshUnreadOnly();
    };
    const interval = window.setInterval(tick, 120000);
    const unsub = subscribeRealtimeInvalidate((d) => {
      const reason = d?.reason || '';
      if (
        reason.startsWith('team-')
        || reason.startsWith('learn-')
        || reason === 'notification'
      ) {
        refresh();
      }
    });
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(interval);
      unsub();
      document.removeEventListener('visibilitychange', tick);
    };
  }, [user, apiClient, refresh, refreshUnreadOnly]);

  const markRead = useCallback(async (id) => {
    if (!apiClient || !id) return;
    await apiClient.post(`/api/notifications/${id}/read`);
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
  }, [apiClient]);

  const markAllRead = useCallback(async () => {
    if (!apiClient) return;
    await apiClient.post('/api/notifications/read-all');
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
  }, [apiClient]);

  const value = useMemo(() => ({
    unreadCount,
    items,
    loading,
    refresh,
    markRead,
    markAllRead,
  }), [unreadCount, items, loading, refresh, markRead, markAllRead]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications requires NotificationProvider');
  return ctx;
}

export function useNotificationsOptional() {
  return useContext(NotificationContext);
}
